import { type ReactNode, useMemo, useState } from 'react'

import type { TraceRecord } from '../store/useObservabilityStore'
import { font } from '../theme/tokens'

type TracePanelProps = {
  tracesByStep: Record<string, TraceRecord[]>
  runId: string
}

type TraceLevel = 'all' | 'info' | 'error' | 'reasoning'
type TraceSource = 'all' | 'wasm' | 'gateway' | 'orchestrator'

type NormalizedTrace = TraceRecord & {
  level: Exclude<TraceLevel, 'all'> | 'info'
  source: Exclude<TraceSource, 'all'> | 'orchestrator'
  thoughtProcess?: string
}

function normalizeTrace(trace: TraceRecord): NormalizedTrace {
  const metadata = trace.metadata || {}
  const rawLevel = String(metadata.level || metadata.trace_level || inferLevel(trace)).toLowerCase()
  const rawSource = String(metadata.source || metadata.trace_source || inferSource(trace)).toLowerCase()
  const thoughtProcess = firstString(
    metadata.thought_process,
    metadata.thoughtProcess,
    metadata.reasoning,
  )

  return {
    ...trace,
    level:
      rawLevel === 'error' || rawLevel === 'reasoning'
        ? rawLevel
        : 'info',
    source:
      rawSource === 'wasm' || rawSource === 'gateway'
        ? rawSource
        : 'orchestrator',
    thoughtProcess,
  }
}

function inferLevel(trace: TraceRecord): string {
  if (trace.errorCode || trace.errorMessage || trace.status === 'failed') {
    return 'error'
  }
  if (trace.metadata && (trace.metadata.thought_process || trace.metadata.thoughtProcess || trace.metadata.reasoning)) {
    return 'reasoning'
  }
  return 'info'
}

function inferSource(trace: TraceRecord): string {
  if (trace.skillId || trace.workerRef) {
    return 'wasm'
  }
  if (trace.metadata && (trace.metadata.gateway || trace.metadata.mcp_gateway)) {
    return 'gateway'
  }
  return 'orchestrator'
}

function firstString(...values: unknown[]): string | undefined {
  for (const value of values) {
    if (typeof value === 'string' && value.trim() !== '') {
      return value
    }
  }
  return undefined
}

function levelTint(level: string) {
  switch (level) {
    case 'error':
      return '#ff7b72'
    case 'reasoning':
      return '#f7b955'
    default:
      return '#7ad1ff'
  }
}

function sourceTint(source: string) {
  switch (source) {
    case 'wasm':
      return '#7ed8a8'
    case 'gateway':
      return '#b59cff'
    default:
      return '#93a4b8'
  }
}

function exportTraceAsPDF(trace: NormalizedTrace) {
  if (typeof window === 'undefined') {
    return
  }
  const win = window.open('', '_blank', 'noopener,noreferrer,width=960,height=720')
  if (!win) {
    return
  }
  const thought = trace.thoughtProcess
    ? `<blockquote style="margin:16px 0;padding:14px 16px;border-left:4px solid #f7b955;background:#fff8e6;color:#4b3a10;white-space:pre-wrap;">${escapeHtml(trace.thoughtProcess)}</blockquote>`
    : ''

  win.document.write(`<!doctype html>
    <html>
      <head>
        <title>Trace ${escapeHtml(trace.id)}</title>
        <style>
          body { font-family: ${font.printSans}; margin: 32px; color: #111827; }
          h1 { margin: 0 0 8px; font-size: 22px; }
          .meta { color: #6b7280; font-size: 12px; margin-bottom: 20px; }
          pre { background: #0b1220; color: #e5edf7; padding: 16px; border-radius: 12px; white-space: pre-wrap; overflow-wrap: anywhere; }
          .pill { display:inline-block; padding:4px 8px; border-radius:999px; font-size:11px; font-weight:700; text-transform:uppercase; margin-right:8px; background:#eef2ff; color:#3730a3; }
        </style>
      </head>
      <body>
        <h1>Trace Export</h1>
        <div class="meta">
          <span class="pill">${escapeHtml(trace.level)}</span>
          <span class="pill">${escapeHtml(trace.source)}</span>
          ${escapeHtml(trace.id)} · ${escapeHtml(trace.startedAt)}
        </div>
        ${thought}
        <h3>stderr</h3>
        <pre>${escapeHtml(trace.stderr || '—')}</pre>
        <h3>stdout</h3>
        <pre>${escapeHtml(trace.stdout || '—')}</pre>
      </body>
    </html>`)
  win.document.close()
  win.focus()
  win.print()
}

function escapeHtml(value: string) {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;')
}

export function TracePanel({ tracesByStep, runId }: TracePanelProps) {
  const [levelFilter, setLevelFilter] = useState<TraceLevel>('all')
  const [sourceFilter, setSourceFilter] = useState<TraceSource>('all')
  const [selectedTraceId, setSelectedTraceId] = useState<string>()

  const traces = useMemo(() => {
    return Object.entries(tracesByStep)
      .flatMap(([stepId, items]) =>
        items.map((trace) => ({
          ...normalizeTrace(trace),
          stepKey: stepId,
        })),
      )
      .sort((a, b) => a.startedAt.localeCompare(b.startedAt))
  }, [tracesByStep])

  const filtered = useMemo(() => {
    return traces.filter((trace) => {
      if (levelFilter !== 'all' && trace.level !== levelFilter) {
        return false
      }
      if (sourceFilter !== 'all' && trace.source !== sourceFilter) {
        return false
      }
      return true
    })
  }, [levelFilter, sourceFilter, traces])

  const selected = filtered.find((trace) => trace.id === selectedTraceId) || filtered[0]

  return (
    <section
      style={{
        background: 'linear-gradient(180deg, rgba(12,17,28,0.94) 0%, rgba(8,12,19,0.98) 100%)',
        border: '1px solid rgba(148, 163, 184, 0.14)',
        borderRadius: 16,
        padding: 20,
        color: '#edf2f7',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, gap: 12 }}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: '0.16em', textTransform: 'uppercase', color: '#93a4b8', marginBottom: 6 }}>
            Traceability Explorer
          </div>
          <div style={{ fontSize: 14, color: '#d7e2f0' }}>Run {runId}</div>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <FilterSelect label="Level" value={levelFilter} onChange={(value) => setLevelFilter(value as TraceLevel)}>
            <option value="all">All Levels</option>
            <option value="info">Info</option>
            <option value="error">Error</option>
            <option value="reasoning">Reasoning</option>
          </FilterSelect>
          <FilterSelect label="Source" value={sourceFilter} onChange={(value) => setSourceFilter(value as TraceSource)}>
            <option value="all">All Sources</option>
            <option value="wasm">Wasm</option>
            <option value="gateway">Gateway</option>
            <option value="orchestrator">Orchestrator</option>
          </FilterSelect>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '340px minmax(0, 1fr)', gap: 16 }}>
        <div
          style={{
            border: '1px solid rgba(148, 163, 184, 0.12)',
            borderRadius: 14,
            overflow: 'hidden',
            background: 'rgba(255,255,255,0.02)',
            maxHeight: 520,
            overflowY: 'auto',
          }}
        >
          {filtered.length === 0 ? (
            <div style={{ padding: 18, color: '#94a3b8' }}>No traces match the current filters.</div>
          ) : (
            filtered.map((trace) => {
              const isActive = selected?.id === trace.id
              return (
                <button
                  key={trace.id}
                  onClick={() => setSelectedTraceId(trace.id)}
                  style={{
                    width: '100%',
                    textAlign: 'left',
                    border: 'none',
                    background: isActive ? 'rgba(91, 192, 255, 0.08)' : 'transparent',
                    borderBottom: '1px solid rgba(148, 163, 184, 0.08)',
                    padding: '14px 16px',
                    cursor: 'pointer',
                    color: '#edf2f7',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
                    <strong style={{ fontSize: 13 }}>{trace.stepId || 'run-level trace'}</strong>
                    <span style={{ fontSize: 11, color: '#8ea0b7' }}>
                      {new Date(trace.startedAt).toLocaleTimeString()}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <TraceBadge label={trace.level} tint={levelTint(trace.level)} />
                    <TraceBadge label={trace.source} tint={sourceTint(trace.source)} />
                  </div>
                </button>
              )
            })
          )}
        </div>

        <div
          style={{
            border: '1px solid rgba(148, 163, 184, 0.12)',
            borderRadius: 14,
            background: 'rgba(255,255,255,0.02)',
            padding: 18,
            minHeight: 420,
          }}
        >
          {!selected ? (
            <div style={{ color: '#94a3b8' }}>Select a trace to inspect.</div>
          ) : (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', marginBottom: 16 }}>
                <div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                    <TraceBadge label={selected.level} tint={levelTint(selected.level)} />
                    <TraceBadge label={selected.source} tint={sourceTint(selected.source)} />
                  </div>
                  <h3
                    style={{
                      margin: 0,
                      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                      fontSize: 16,
                    }}
                  >
                    {selected.id}
                  </h3>
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                  <ActionButton
                    label="Copy Trace ID"
                    onClick={async () => {
                      if (navigator?.clipboard) {
                        await navigator.clipboard.writeText(selected.id)
                      }
                    }}
                  />
                  <ActionButton label="Export to PDF" onClick={() => exportTraceAsPDF(selected)} />
                </div>
              </div>

              {selected.thoughtProcess ? (
                <blockquote
                  style={{
                    margin: '0 0 18px',
                    padding: '14px 18px',
                    borderLeft: '4px solid #f7b955',
                    background: 'rgba(247, 185, 85, 0.08)',
                    color: '#f7e5b0',
                    borderRadius: 12,
                    whiteSpace: 'pre-wrap',
                    fontStyle: 'italic',
                  }}
                >
                  {selected.thoughtProcess}
                </blockquote>
              ) : null}

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12, marginBottom: 16 }}>
                <MetaCard label="Started" value={new Date(selected.startedAt).toLocaleString()} />
                <MetaCard label="Status" value={selected.status} />
                <MetaCard label="Execution" value={selected.executionMs ? `${selected.executionMs} ms` : '—'} />
                <MetaCard label="Step" value={selected.stepId || 'run-level'} />
              </div>

              <TraceTextBlock title="stderr" value={selected.stderr} tone="stderr" />
              <TraceTextBlock title="stdout" value={selected.stdout} tone="stdout" />
            </>
          )}
        </div>
      </div>
    </section>
  )
}

function FilterSelect({
  label,
  value,
  onChange,
  children,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  children: ReactNode
}) {
  return (
    <label style={{ display: 'grid', gap: 6 }}>
      <span style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.14em', color: '#8ea0b7', fontWeight: 800 }}>
        {label}
      </span>
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        style={{
          background: '#08101b',
          color: '#edf2f7',
          border: '1px solid rgba(148, 163, 184, 0.16)',
          borderRadius: 10,
          padding: '9px 12px',
        }}
      >
        {children}
      </select>
    </label>
  )
}

function TraceBadge({ label, tint }: { label: string; tint: string }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '4px 8px',
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 800,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: tint,
        background: `${tint}18`,
        border: `1px solid ${tint}33`,
      }}
    >
      {label}
    </span>
  )
}

function ActionButton({ label, onClick }: { label: string; onClick: () => void | Promise<void> }) {
  return (
    <button
      onClick={() => void onClick()}
      style={{
        border: '1px solid rgba(148, 163, 184, 0.18)',
        background: 'rgba(255,255,255,0.03)',
        color: '#edf2f7',
        borderRadius: 10,
        padding: '9px 12px',
        fontWeight: 700,
        cursor: 'pointer',
      }}
    >
      {label}
    </button>
  )
}

function MetaCard({ label, value }: { label: string; value: string }) {
  return (
    <div
      style={{
        border: '1px solid rgba(148, 163, 184, 0.12)',
        borderRadius: 12,
        padding: '12px 14px',
        background: 'rgba(255,255,255,0.02)',
      }}
    >
      <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#8ea0b7', marginBottom: 8 }}>
        {label}
      </div>
      <div style={{ fontSize: 13, color: '#edf2f7' }}>{value}</div>
    </div>
  )
}

function TraceTextBlock({ title, value, tone }: { title: string; value?: string; tone: 'stderr' | 'stdout' }) {
  const background = tone === 'stderr' ? '#120d12' : '#0a131d'
  const color = tone === 'stderr' ? '#f3c0d3' : '#dce7f3'
  return (
    <div style={{ marginTop: 14 }}>
      <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#8ea0b7', marginBottom: 8 }}>
        {title}
      </div>
      <pre
        style={{
          margin: 0,
          padding: 16,
          background,
          color,
          borderRadius: 12,
          border: '1px solid rgba(148, 163, 184, 0.1)',
          whiteSpace: 'pre-wrap',
          overflowWrap: 'anywhere',
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
          fontSize: 12.5,
          lineHeight: 1.55,
        }}
      >
        {value || '—'}
      </pre>
    </div>
  )
}
